import { useRef, useEffect, useMemo } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { Grid, Typography } from '@mui/material';
import useWindowSize from '@shared/hooks/useWindowsSize';
import { useGoogleMaps } from '@shared/providers/GoogleMapsProvider';
import { MAP_ID_MAPS } from '@shared/conf/env';

const MapContainer = ({
  center,
  zoom = 15,
  fullWidth = false,
  expanded = false,
  fillParent = false,
  multipleMarkers = [],
  customMarker = null,
  onMarkerClick,
  onMapClick,
  selectedMarkerId,
  children,
}) => {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 1280;

  const { isLoaded } = useGoogleMaps();

  const containerStyle = useMemo(() => {
    if (expanded) {
      return { position: 'relative', width: '100%', height: '100%' };
    }

    if (isMobile) {
      const size = screenSize.width + 100;
      return {
        position: 'relative',
        height: `${size}px`,
        width: `${size}px`,
        margin: '0 auto',
      };
    }
    if (fillParent) {
      return { position: 'relative', height: '90vh', width: '100%' };
    }

    return {
      position: 'relative',
      height: '90vh',
      width: fullWidth ? '80vw' : '60vw',
      margin: '0 auto',
    };
  }, [isMobile, screenSize.width, fullWidth, expanded, fillParent]);

  const addMarker = (map, markerData, title) => {
    const { latitude, longitude, lat, lng } = markerData;
    const latVal = Number(latitude ?? lat);
    const lngVal = Number(longitude ?? lng);

    if (!latVal || !lngVal) return;

    const isSelected =
      Boolean(selectedMarkerId) &&
      (markerData.id ?? markerData.name) === selectedMarkerId;

    let content;
    if (customMarker) {
      const wrapper = document.createElement('div');
      wrapper.style.position = 'relative';
      wrapper.style.width = '32px';
      wrapper.style.height = '32px';
      // AdvancedMarkerElement ancla el contenido por su borde inferior,
      // el Marker viejo lo anclaba por el centro (Point(16,16)).
      wrapper.style.transform = 'translateY(50%)';

      const img = document.createElement('img');
      img.src = customMarker;
      img.style.width = '32px';
      img.style.height = '32px';
      img.style.display = 'block';
      wrapper.appendChild(img);

      if (isSelected && title) {
        img.style.filter = 'drop-shadow(0 3px 5px rgba(0,0,0,.35))';

        const chip = document.createElement('div');
        chip.textContent = title;
        chip.style.position = 'absolute';
        chip.style.bottom = '100%';
        chip.style.left = '50%';
        chip.style.transform = 'translateX(-50%)';
        chip.style.marginBottom = '4px';
        chip.style.background = '#241f1a';
        chip.style.color = '#fff';
        chip.style.fontSize = '11px';
        chip.style.fontWeight = '600';
        chip.style.whiteSpace = 'nowrap';
        chip.style.padding = '3px 8px';
        chip.style.borderRadius = '6px';
        chip.style.fontFamily = 'Open Sans, sans-serif';
        wrapper.appendChild(chip);
      }

      content = wrapper;
    }

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position: { lat: latVal, lng: lngVal },
      title,
      content,
    });

    if (onMarkerClick) {
      marker.addListener('click', () => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onMarkerClick(markerData);
      });
    }

    markersRef.current.push(marker);
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center?.lat || !center?.lng) return;

    // Limpia los marcadores anteriores
    markersRef.current.forEach(m => {
      m.map = null;
    });
    markersRef.current = [];

    if (multipleMarkers.length > 0) {
      multipleMarkers.forEach(m => addMarker(map, m, m.name || ''));
    } else {
      addMarker(map, center, 'Ubicación');
    }

    map.panTo({ lat: Number(center.lat), lng: Number(center.lng) });
  }, [center, multipleMarkers, customMarker, onMarkerClick, selectedMarkerId]);

  if (!isLoaded) {
    return <Typography>Cargando mapa...</Typography>;
  }

  if (!center?.lat || !center?.lng) {
    return (
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ width: 300, height: 150 }}
      >
        <Typography variant="h4">¡Lo sentimos!</Typography>
        <Grid
          sx={{ width: '96%', my: 1.25, borderBottom: '1px solid #858282' }}
        />
        <Typography variant="h6" align="center">
          No pudimos encontrar la ubicación
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid
      container
      justifyContent="center"
      sx={expanded ? { height: '100%' } : undefined}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        options={{ mapId: MAP_ID_MAPS, fullscreenControl: false }}
        zoom={zoom}
        center={{ lat: Number(center.lat), lng: Number(center.lng) }}
        onLoad={map => {
          mapRef.current = map;

          if (multipleMarkers.length > 0) {
            multipleMarkers.forEach(m => addMarker(map, m, m.name || ''));
          } else {
            addMarker(map, center, 'Ubicación');
          }

          map.panTo({ lat: Number(center.lat), lng: Number(center.lng) });
        }}
        onClick={onMapClick}
      >
        {children}
      </GoogleMap>
    </Grid>
  );
};

export default MapContainer;
